import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { isStabilizedBridge } from '@roc/feature-utils';
import { insertIf, PURCHASE, REFINANCE } from '@roc/feature-utils';
import { amortizationOptions } from '@roc/feature-loans';
import { LoanSubType, prepaymentPenaltyOptions } from './constants';
import { addressToString } from './quoteUtils';
import { RentalPortfolioInformationFormStore } from '../stores/rentalPortfolio/rentalPortfolioInformationFormStore';
import { RentalPortfolioStore } from '../stores/rentalPortfolioStore';

// Calculate release amount for a property
export function calculateReleaseAmount(
  property: any,
  propertyCount: number,
  loanAmount: number,
  aggregateArvOrAsis: number
) {
  if (propertyCount > 1) {
    const arvLoanToValue =
      aggregateArvOrAsis === 0 ? 0 : loanAmount / aggregateArvOrAsis;
    if (property.financials?.marketValueAfterCompletion != null) {
      return Number(
        property.financials?.marketValueAfterCompletion * arvLoanToValue * 1.2
      ).toFixed(2);
    } else {
      return Number(
        ((property.financials?.purchasePrice || 0) * arvLoanToValue * 1.2).toFixed(2)
      );
    }
  } else {
    return Number(((loanAmount || 0) * 1.2).toFixed(2));
  }
}

// Calculate property allocated amount
export function calculatePropertyAllocatedAmount(
  property: any,
  propertyCount: number,
  loanAmount: number,
  aggregateArvOrAsis: number
) {
  if (propertyCount > 1) {
    const arvLoanToValue =
      aggregateArvOrAsis === 0 ? 0 : loanAmount / aggregateArvOrAsis;
    if (property.financials?.marketValueAfterCompletion != null) {
      return Number(
        (property.financials?.marketValueAfterCompletion * arvLoanToValue).toFixed(2)
      );
    } else {
      return Number(
        ((property.financials?.purchasePrice || 0) * arvLoanToValue).toFixed(2)
      );
    }
  } else {
    return Number((loanAmount || 0).toFixed(2));
  }
}

export const generateLoanDetailsTableData = (
  outputs,
  loanPurpose,
  loanSubType
) => {
  const purchasePriceOrPayoffAmount =
    PURCHASE === loanPurpose
      ? outputs?.totalPurchasePrice
      : outputs?.totalPayoffAmount;

  const categories = [
    {
      name: 'Purchase Price or Payoff Amount',
      fees: [
        {
          name:
            PURCHASE === loanPurpose
              ? 'Acquisition Price'
              : 'Existing Debt Payoff',
          amount: formatCurrency(purchasePriceOrPayoffAmount),
        },
        {
          name: isStabilizedBridge(loanSubType)
            ? 'Loan Amount'
            : 'Initial Loan Amount',
          amount: formatCurrency(outputs?.initialLoanAmount),
        },
      ],
      total: formatCurrency(purchasePriceOrPayoffAmount),
    },
    {
      name: 'Reserves & Escrows',
      fees: [
        {
          name: 'Interest Reserve (Minimum prepaid)',
          amount: formatCurrency(outputs?.interestReserve),
        },
      ],
      total: formatCurrency(outputs?.interestReserve),
    },
    {
      name: 'Lender Fees',
      fees: (outputs?.lenderFees ?? []).map(fee => ({
        name: fee.feeName,
        amount: formatCurrency(fee.feeAmount),
      })),
      total: formatCurrency(outputs?.totalLenderFees),
    },
    {
      name: 'Estimated Third Party Fees',
      fees: [
        ...insertIf(outputs?.brokerOriginationFees > 0, [
          {
            name: 'Broker Origination Fees',
            amount: formatCurrency(outputs?.brokerOriginationFees),
          },
        ]),
        {
          name: 'Title, Insurance and Other Estimated Costs',
          amount: formatCurrency(outputs?.thirdPartyCosts),
        },
      ],
      total: formatCurrency(
        outputs?.brokerOriginationFees + outputs?.thirdPartyCosts
      ),
    },
  ];

  const total =
    purchasePriceOrPayoffAmount +
    outputs.interestReserve +
    outputs?.totalLenderFees +
    outputs?.brokerOriginationFees +
    outputs?.thirdPartyCosts;

  return { categories, total: formatCurrency(total) };
};

export const mapDownloadableQuoteData = (store: any) => {
  const loanEconomicsFormValues = store.loanEconomicsStore.loanEconomicsFormStore.getFormValues();
  const properties: any[] = store.propertiesStore.getProperties();
  const propertyCount = properties.length;
  // Sum afterRepairValue or asIsValue for all properties
  const aggregateArvOrAsis = properties.reduce((sum, p) => {
    return sum + Number(p.financials?.marketValueAfterCompletion || p.financials?.purchasePrice || 0);
  }, 0);

  const calculatedFields = store.loanEconomicsStore.calculateFields();
  const borrowers = store.borrowersStore.getBorrowers();
  const loanAmount = calculatedFields?.totalLoanAmount || 0;

  // Add releaseAmount to each property
  const propertiesWithReleaseAmount = properties.map(property => ({
    ...property,
    releaseAmount: calculateReleaseAmount(
      property,
      propertyCount,
      loanAmount,
      aggregateArvOrAsis
    ),
    propertyLoanAllocatedAmount: calculatePropertyAllocatedAmount(
      property,
      propertyCount,
      loanAmount,
      aggregateArvOrAsis
    ),
  }));

  let lenderFees = store.loanEconomicsStore.feeStores.map(feeStore => ({
    feeName: feeStore.getFeeValues(calculatedFields.totalLoanAmount).name,
    feeAmount:
      feeStore.getFeeValues(calculatedFields.totalLoanAmount).lenderAmount ?? 0,
  }));

  lenderFees = [
    { feeName: 'Origination Fee', feeAmount: calculatedFields.originationFees },
    ...lenderFees,
  ]

  const thirdPartyFees = [
    {
      feeName: 'Broker Origination Fees',
      feeAmount: calculatedFields.brokerOriginationFees,
    },
    {
      feeName: 'Title, Insurance and Other Estimated Costs',
      feeAmount: calculatedFields.thirdPartyCosts,
    },
  ];

  return {
    loanSubtype: store.loanSubtype,
    loanPurpose: store.propertiesStore.getProperties()?.[0]?.loanPurpose,
    properties: propertiesWithReleaseAmount, // include releaseAmount and allocatedAmount
    borrowers,
    entity: store.entityStore.getEntity(),
    ltv: calculatedFields.calculatedLTV,
    arltv: calculatedFields.afterRepairLTV,
    ltfc: calculatedFields.ltfc ?? calculatedFields.calculatedLTFC,
    ltcCatchupDraw: calculatedFields?.ltcCatchupDrawAmount,
    brokerFee: calculatedFields.brokerPoints
      ? `${formatCurrency(
        calculatedFields?.brokerOriginationFees
      )} (${formatPercentage(loanEconomicsFormValues?.brokerPoints)})`
      : null,

    interestPaymentType: loanEconomicsFormValues?.interestAccrualMethod,
    interestReserve: calculatedFields.interestReserve,
    initialLoanAmount: calculatedFields.initialAdvance,
    constructionHoldback: calculatedFields.constructionHoldback,
    constructionReserve: calculatedFields.constructionReserve,
    totalLoanAmount: calculatedFields.totalLoanAmount,
    totalPurchasePrice: calculatedFields.totalPurchasePrice,
    lenderFees: lenderFees,
    totalLenderFees: calculatedFields.totalLenderFees,
    thirdPartyCosts: calculatedFields.thirdPartyCosts,
    thirdPartyFees: thirdPartyFees,
    brokerOriginationFees: calculatedFields.brokerOriginationFees,
    totalPayoffAmount: calculatedFields.totalPayoffAmount,
    economics: store.loanEconomicsStore.getLoanEconomics(),
  };
};

export const mapDownloadableQuoteDataTerm = (store: any) => {
  const loanEconomicsFormValues = store.loanEconomicsStore.loanEconomicsFormStore.getFormValues();

  const calculatedFields = store.loanEconomicsStore.calculateFields();
  const propertyData = store.loanSubtype === LoanSubType.RENTAL_PORTFOLIO ? termPortfolioData(store) : termPropertiesData(store);
  const broker = store.loanEconomicsStore.getBroker();

  const borrowers = store.borrowersStore.getBorrowers();

  let lenderFees = store.loanEconomicsStore.feeStores.map(feeStore => ({
    feeName: feeStore.getFeeValues(calculatedFields.totalLoanAmount).name,
    feeAmount:
      feeStore.getFeeValues(calculatedFields.totalLoanAmount).lenderAmount ?? 0,
  }));
  lenderFees = [
    { feeName: 'Origination Fee', feeAmount: calculatedFields.originationFees },
    ...lenderFees,
  ]

  const thirdPartyFees = [
    {
      feeName: 'Broker Origination Fees',
      feeAmount: calculatedFields.brokerOriginationFees,
    },
    {
      feeName: 'Title, Insurance and Other Estimated Costs',
      feeAmount: calculatedFields.thirdPartyCosts,
    },
  ];

  const pricingOptions = [
    {
      loanToValue: calculatedFields.loanToValue,
      loanAmount: calculatedFields.totalLoanAmount,
      loanPurpose: propertyData.loanPurpose,
      amortizationType: amortizationOptions.find(
        opt => opt.value == loanEconomicsFormValues.amortizationType
      )?.label,
      prePaymentPenalty: prepaymentPenaltyOptions.find(
        opt => opt.value == loanEconomicsFormValues.prePaymentPenalty
      )?.label,
      rateType: loanEconomicsFormValues.rateType,
      interestRate: loanEconomicsFormValues.interestRate,
      estimatedMonthlyPayment: calculatedFields.monthlyPIPayment,
      brokerFee: broker?.brokerPoints ?? 0,
    },
  ];

  const borrowersExperience = borrowers.length > 0 ? Math.max(...borrowers.map(b => b?.experience?.rentalExperience)) : '';
  const estimatedCreditScore = borrowers.length > 0 ? Math.max(...borrowers.map(b => b?.credit?.ficoScore)) : '';
  const citizenshipStatus = borrowers.length > 0 ? borrowers?.[0]?.personal?.citizenshipStatus : '';

  Math.max(...borrowers.map(b => b?.experience?.rentalExperience));

  return {
    loanSubtype: store.loanSubtype,
    loanPurpose: propertyData.loanPurpose,
    properties: propertyData,
    borrowers,
    borrowersExperience,
    estimatedCreditScore,
    citizenshipStatus,
    entity: store.entityStore.getEntity(),
    ltv: calculatedFields.calculatedLTV,
    arltv: calculatedFields.afterRepairLTV,
    ltfc: calculatedFields.ltfc ?? calculatedFields.calculatedLTFC,
    ltcCatchupDraw: calculatedFields?.ltcCatchupDrawAmount,
    brokerFee: calculatedFields.brokerPoints
      ? `${formatCurrency(
        calculatedFields?.brokerOriginationFees
      )} (${formatPercentage(loanEconomicsFormValues?.brokerPoints)})`
      : null,

    interestPaymentType: loanEconomicsFormValues?.interestAccrualMethod,
    interestReserve: calculatedFields.interestReserve,
    initialLoanAmount: calculatedFields.initialAdvance,
    constructionHoldback: calculatedFields.constructionHoldback,
    constructionReserve: calculatedFields.constructionReserve,
    totalLoanAmount: calculatedFields.totalLoanAmount,
    totalPurchasePrice: calculatedFields.totalPurchasePrice,
    lenderFees: lenderFees,
    totalLenderFees: calculatedFields.totalLenderFees,
    thirdPartyCosts: calculatedFields.thirdPartyCosts,
    thirdPartyFees: thirdPartyFees,
    brokerOriginationFees: calculatedFields.brokerOriginationFees,
    totalPayoffAmount: calculatedFields.totalPayoffAmount,
    economics: store.loanEconomicsStore.getLoanEconomics(),
    pricingOptions: pricingOptions,
  };
};

export const termPropertiesData = (store: any) => {
  const properties: any[] = store.propertiesStore.getProperties();

  const property = properties[0];
  return {
    totalEstimatedAsIsValue: property.financials.currentMarketValue,
    totalAcquisitionCost: property.financials.purchasePrice,
    acquisitionDate: property.dates.purchaseDate,
    completedRenovations: property.financials.renovationBudget,
    monthlyExpenses: property.financials.annualTaxes + property.financials.annualInsurance + property.financials.annualHOA,
    monthlyRent: property.financials.monthlyMarketRent,
    propertyType: property.propertyType,
    propertyAddress: addressToString(property.address),
    propertyCounty: property.address.county,
    loanPurpose: property.loanPurpose,
  };
}

export const termPortfolioData = (store: RentalPortfolioStore) => {
  const portfolioStore = store.portfolioInformationFormStore;
  const portfolio = portfolioStore.getLoanPortfolioInformation();
  return {
    totalEstimatedAsIsValue: portfolioStore.totalEstimatedAsIsValue,
    totalAcquisitionCost: portfolio.aggregates.acquisitionPrice,
    acquisitionDate: portfolio.dates.lastPurchaseDate,
    completedRenovations: portfolio.aggregates.capitalImprovements,
    monthlyExpenses: portfolio.aggregates.totalGrossAnnualTaxes + portfolio.aggregates.totalGrossAnnualInsurance + portfolio.aggregates.totalAnnualHOADues,
    monthlyRent: portfolio.aggregates.totalGrossMonthlyRent,
    propertyType: portfolioStore.rentalPortfoliosPropertyTypesAsString,
    propertyAddress: `${portfolioStore.totalNoOfProperties} Property Portfolio in ${portfolio.propertyState}`,
    propertyCounty: portfolio.propertyCounties.map(county => county.replace(/, \w{2}$/, "")).join(", "),
    loanPurpose: portfolio.loanPurpose,
  };
}